<script setup lang="ts">
/**
 * The hull customise window: eight slots, what is fitted, and what could be.
 *
 * Every number shown here — price, affordability, the before-and-after stat
 * delta — is the server's. The client decides nothing about what a part is
 * worth; it renders what it was handed. A disabled button is a courtesy, the
 * check in `GarageService` is the enforcement.
 *
 * **Everything in it is about one hull, including ownership.** The window was
 * always per hull because fittings are; parts are now *owned* per hull too, so
 * `owned` on a card means "this tank has one" rather than "this account does",
 * and the same part on a second tank is a second purchase at full price. That is
 * the one thing here a player can mistake for a bug, so the window says it out
 * loud — see the rule line in the template, which is the most load-bearing
 * sentence in this file.
 */
import { computed, ref } from 'vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import type { GarageCustomise, GaragePart } from '@/net/api';
import { askToBuy, type PurchaseStats } from '@/state/confirm-purchase';
import { SPEC_STATS } from './stat-display';

const props = defineProps<{
  customise: GarageCustomise;
  credits: number;
  /** The other balance the shelf charges in — two of the four cards are diamonds. */
  diamonds: number;
  busyId: string | null;
}>();

const emit = defineEmits<{
  close: [];
  /**
   * The hull rides with the part, because it is half of what is being bought.
   *
   * `fit` does not carry one and does not need to — the screen reads it back off
   * the open window, and a fitting made against the wrong hull would be visibly
   * wrong and freely undone. A *purchase* has neither property: the id the
   * confirmation named and the id the server charges for must be the same one,
   * and the only way to guarantee that is to send the id the dialog was built
   * from rather than re-read the store afterwards.
   */
  buy: [hullId: string, partId: string];
  fit: [partId: string];
  showResearch: [nodeId: string];
}>();

/** Which slot's shelf is open. The first, until someone picks another. */
const openSlot = ref(props.customise.slots[0]?.id ?? '');

const slot = computed(
  () => props.customise.slots.find((s) => s.id === openSlot.value) ?? props.customise.slots[0],
);

/**
 * What is bolted into a slot, as a label.
 *
 * The stock fitting is deliberately **not** one of the four cards — see
 * `partsForSlot` — so a slot nothing has been bought for finds nothing in the
 * shelf and reads "Standard issue" from `stockFitted` instead. Looking it up in
 * `parts` and falling back to an em dash, as this did, would have told a player
 * with an unmodified hull that the slot was empty.
 */
const fittedIn = (slotId: string): string => {
  const s = props.customise.slots.find((x) => x.id === slotId);
  if (!s) return '—';
  if (s.stockFitted) return 'Standard issue';
  return s.parts.find((p) => p.id === s.fittedPartId)?.name ?? '—';
};

/** Whether a part is bought with diamonds. Mirrors `isDiamondPart` server-side. */
const isDiamond = (part: GaragePart): boolean =>
  typeof part.diamondPrice === 'number' && part.diamondPrice > 0;

/**
 * What a part costs — the figure the server will actually debit, without a unit.
 *
 * The unit is a drawn mark now (`CurrencyIcon`), so it cannot be baked into the
 * string; `kindOf` says which one to draw. Same split the item cards made.
 */
const costOf = (part: GaragePart): string =>
  isDiamond(part) ? String(part.diamondPrice) : String(part.price);

const kindOf = (part: GaragePart): 'credits' | 'diamonds' =>
  isDiamond(part) ? 'diamonds' : 'credits';

/** How much more is needed, again unitless. */
const shortBy = (part: GaragePart): number =>
  isDiamond(part) ? part.diamondPrice! - props.diamonds : part.price - props.credits;

/** A stat that goes up is a gain, except reload — none of which parts touch. */
const better = (d: { from: number; to: number }) => d.to > d.from;

const round = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, ''));

/**
 * The tank this window is about, in the words the dialog and the rule use.
 *
 * Carries its own article so the fallback stays grammatical: a catalogue that
 * has dropped the hull between builds gives "this hull", not "the null".
 */
const hullLabel = computed(() =>
  props.customise.hullName ? `the ${props.customise.hullName}` : 'this hull',
);

/**
 * Buying a part asks first; fitting one never does.
 *
 * That split is the window's whole economy stated as an interaction: owning and
 * fitting are different facts, and only the first of them costs credits. A
 * confirmation on Fit would put a modal in front of the reversible half.
 *
 * The dialog is raised here, in the one handler, rather than beside the button
 * in the `v-for` — thirty-two parts through one function, exactly as the
 * catalogue grid's three spends go through `GarageScreen`.
 *
 * **The hull is named, and that reversed.** It used to be deliberately left out,
 * because ownership was per account and "for the Bulwark" would have sold a
 * second purchase that did not exist. Ownership is per hull now, so the opposite
 * is true: this really is a purchase for one chassis, and a dialog that named
 * only the part would be quoting a price for something broader than what it
 * charges for. It reads *Tier 2 powerplant for the Bulwark* — the rule itself is
 * stated once at the top of the window rather than repeated here, because a
 * player meets it while shopping and should not first learn it at the till.
 */
async function buy(part: GaragePart): Promise<void> {
  const slotName = props.customise.slots.find((s) => s.id === part.slot)?.name;
  const hullId = props.customise.hullId;
  // No hull, nothing to buy it for. Unreachable while the window is open on a
  // real chassis, and a silent return rather than a dialog: a spend whose
  // subject the screen cannot state must not be offered at all.
  if (!hullId) return;

  const kind = slotName ? `Tier ${part.tier} ${slotName.toLowerCase()}` : `Tier ${part.tier} part`;
  const detail = `${kind} for ${hullLabel.value}`;
  if (
    !(await askToBuy({
      item: part.name,
      detail,
      // The part's own render, framed exactly as the card behind this dialog
      // frames it — `cover`, not `contain`: these images carry their own pale
      // studio ground, and letterboxing one inside a dark modal turns it into a
      // white slab that reads as a loading error.
      ...(part.image ? { art: { kind: 'image' as const, src: part.image } } : {}),
      stats: statsOf(part),
      // The currency the *server* will move, decided by the part rather than by
      // this screen — `buyPart` reads `diamondPrice` and nothing the client sends.
      // A diamond part's `price` is a valuation nobody is ever charged, so quoting
      // it here would be the dialog naming a figure the ledger never records.
      price: isDiamond(part) ? part.diamondPrice! : part.price,
      currency: isDiamond(part) ? 'diamonds' : 'credits',
    }))
  ) {
    return;
  }
  emit('buy', hullId, part.id);
}

/**
 * A part's before-and-after, as the dialog's stat block.
 *
 * `part.delta` is **computed server-side** by running the real `applyParts`
 * against a hypothetical fitting — the whole reason this window exists in the
 * shape it does — so this only relabels those figures with the descriptors the
 * rest of the garage draws bars from. A key with no descriptor is skipped rather
 * than given an invented bar scale, and a part that moves nothing this table
 * describes shows no block at all.
 *
 * `SPEC_STATS` rather than one of the two halves, because a part may move a hull
 * stat (a powerplant) or a turret one (the ring).
 */
function statsOf(part: GaragePart): PurchaseStats | undefined {
  const rows = part.delta.filter((d) => SPEC_STATS.some((s) => s.key === d.key));
  if (rows.length === 0) return undefined;
  return {
    descriptors: SPEC_STATS.filter((s) => rows.some((d) => d.key === s.key)),
    values: Object.fromEntries(rows.map((d) => [d.key, d.to])),
    against: Object.fromEntries(rows.map((d) => [d.key, d.from])),
  };
}

function actionFor(part: GaragePart): 'fitted' | 'fit' | 'buy' | 'locked' | 'poor' {
  if (part.fitted) return 'fitted';
  if (part.locked) return 'locked';
  if (part.owned) return 'fit';
  return part.affordable ? 'buy' : 'poor';
}
</script>

<template>
  <div class="scrim" data-test="customise-window" @click.self="emit('close')">
    <section class="window">
      <header>
        <div>
          <h2>Customise</h2>
          <p class="hull">
            <span data-test="customise-hull">{{ customise.hullName ?? 'No hull fitted' }}</span>
            <span v-if="!customise.equipped" class="not-fitted" data-test="customise-not-equipped">
              Not currently fitted
            </span>
          </p>
        </div>
        <button
          type="button"
          class="close"
          data-test="customise-close"
          aria-label="Close"
          @click="emit('close')"
        >
          ✕
        </button>
      </header>

      <p class="rule" data-test="customise-per-hull">
        Parts are bought per hull. Anything bought here belongs to {{ hullLabel }} alone —
        fitting the same part to another tank means buying it again for that tank.
      </p>

      <nav class="slots">
        <button
          v-for="s in customise.slots"
          :key="s.id"
          class="slot"
          :class="{ active: s.id === openSlot }"
          :data-test="`slot-${s.id}`"
          @click="openSlot = s.id"
        >
          <span class="slot-name">{{ s.name }}</span>
          <span class="slot-fitted">{{ fittedIn(s.id) }}</span>
        </button>
      </nav>

      <div v-if="slot" class="shelf">
        <p class="slot-blurb">{{ slot.blurb }}</p>

        <ul class="parts">
          <li
            v-for="part in slot.parts"
            :key="part.id"
            class="part"
            :class="{
              fitted: part.fitted,
              locked: part.locked,
              buyable: !part.fitted && !part.locked && !part.owned,
            }"
            :data-test="`part-${part.id}`"
          >
            <div class="art">
              <img v-if="part.image" :src="part.image" :alt="part.name" />
              <div v-else class="art-placeholder" aria-hidden="true">
                <span>{{ slot.name }}</span>
              </div>
            </div>

            <div class="detail">
              <div class="part-head">
                <strong>{{ part.name }}</strong>
                <span class="tag" :class="isDiamond(part) ? 'premium' : 'tier'">
                  {{ part.grade === 'expensive' ? 'Heavy' : 'Light' }}
                  · {{ isDiamond(part) ? 'diamonds' : 'credits' }}
                </span>
              </div>
              <p class="blurb">{{ part.blurb }}</p>

              <ul v-if="part.delta.length" class="delta">
                <li v-for="d in part.delta" :key="d.key" :class="better(d) ? 'up' : 'down'">
                  <span class="delta-label">{{ d.label }}</span>
                  <span class="delta-value">
                    {{ round(d.from) }} <span class="arrow">→</span> {{ round(d.to) }}
                  </span>
                </li>
              </ul>
              <p v-else class="delta-none">No change from what is fitted.</p>
            </div>

            <div class="action">
              <button
                v-if="actionFor(part) === 'fitted'"
                type="button"
                class="fit fitted-lock"
                disabled
                aria-label="Fitted"
                :data-test="`fitted-${part.id}`"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 4.7" /></svg>
                Fit
              </button>

              <button
                v-else-if="actionFor(part) === 'fit'"
                type="button"
                class="fit"
                :disabled="busyId === part.id"
                :data-test="`fit-${part.id}`"
                @click="emit('fit', part.id)"
              >
                Fit
              </button>

              <button
                v-else-if="actionFor(part) === 'buy'"
                type="button"
                class="buy"
                :class="{ gem: isDiamond(part) }"
                :disabled="busyId === part.id"
                :data-test="`buy-${part.id}`"
                @click="buy(part)"
              >
                Buy · {{ costOf(part) }} <CurrencyIcon :kind="kindOf(part)" />
              </button>

              <p v-else-if="actionFor(part) === 'locked'" class="blocked">
                <button
                  v-if="part.requiredResearchId"
                  type="button"
                  class="gate-link"
                  :data-action="`customise-gate-${part.id}`"
                  @click="emit('showResearch', part.requiredResearchId)"
                >
                  Research:<br /><strong>{{ part.requiredResearchName ?? 'further research' }}</strong>
                </button>
                <template v-else>{{ part.lockedReason }}</template>
              </p>

              <p v-else class="blocked">
                Needs <strong class="missing">{{ shortBy(part) }}</strong>
                <CurrencyIcon :kind="kindOf(part)" /> more
              </p>
            </div>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(6, 8, 11, 0.72);
  z-index: 40;
}

.window {
  width: min(60rem, 94vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  background: #12151a;
  overflow: hidden;
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.2rem 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

h2 {
  margin: 0;
  font-size: 1.05rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.hull {
  margin: 0.15rem 0 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  color: #e8722c;
  font-size: 0.85rem;
}

.not-fitted {
  padding: 0.1rem 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 2px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.close {
  display: grid;
  flex: none;
  place-items: center;
  width: 2.2rem;
  height: 2.2rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.35rem;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}

.close:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.3);
}

.close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(232, 114, 44, 0.6);
}

/*
 * Above the slot strip rather than inside a shelf, so it is read once on the way
 * in and cannot be skipped by opening a different slot. Quiet type: it is a rule
 * to be understood, not a warning to be alarmed by — the accent is spent on the
 * hull name in the header and on the prices.
 */
.rule {
  margin: 0;
  padding: 0.55rem 1.2rem 0.7rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.74rem;
  line-height: 1.45;
}

.slots {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.07);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.slot {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  align-items: flex-start;
  padding: 0.55rem 0.7rem;
  border: 0;
  border-radius: 0;
  background: #12151a;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease;
}

.slot:hover:not(.active) {
  background: #171b21;
}

.slot:hover:not(.active) .slot-name {
  color: rgba(255, 255, 255, 0.75);
}

.slot:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.35);
}

.slot.active {
  background: #1b2027;
  box-shadow: inset 0 2px 0 #e8722c;
}

.slot.active .slot-name {
  color: rgba(232, 114, 44, 0.85);
}

.slot.active .slot-fitted {
  color: #fff;
}

.slot-name {
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.12s ease;
}

.slot-fitted {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.88);
}

.shelf {
  overflow-y: auto;
  padding: 0.9rem 1.2rem 1.2rem;
}

.slot-blurb {
  margin: 0 0 0.9rem;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.8rem;
  line-height: 1.4;
}

.parts {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.part {
  display: grid;
  grid-template-columns: 5.5rem 1fr auto;
  gap: 0.9rem;
  align-items: center;
  padding: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.02);
  transition: border-color 0.12s ease, background 0.12s ease;
}

/* Owned or affordable and not yet bought — the one state on this shelf that
   invites a click, so it is the one that answers a hover. The same pale-steel
   language `ResearchTree`'s own `.node:hover` uses, rather than the accent —
   the accent stays reserved for what is actually fitted. */
.part.buyable:hover {
  border-color: rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.045);
}

/* Fitted: a green ring and wash — what is on the hull now. */
.part.fitted {
  border-color: rgba(110, 200, 125, 0.7);
  background: rgba(110, 200, 125, 0.08);
  box-shadow: inset 0 0 0 1px rgba(110, 200, 125, 0.22);
}

.part.locked {
  opacity: 0.55;
}

.art {
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 0.3rem;
  overflow: hidden;
  background: #d9d9d9;
}

.art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.art-placeholder {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  padding: 0.3rem;
  background: repeating-linear-gradient(
    -45deg,
    #23282f,
    #23282f 6px,
    #1c2027 6px,
    #1c2027 12px
  );
  color: rgba(255, 255, 255, 0.42);
  font-size: 0.58rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  text-align: center;
}

.part-head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.tag {
  font-size: 0.6rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

.tag.tier {
  color: #e8722c;
}

.blurb {
  margin: 0.2rem 0 0.45rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.78rem;
  line-height: 1.35;
}

.delta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.74rem;
}

.delta li {
  display: flex;
  gap: 0.4rem;
  font-variant-numeric: tabular-nums;
}

.delta-label {
  color: rgba(255, 255, 255, 0.45);
}

.delta li.up .delta-value {
  color: #6fcf7f;
}

.delta li.down .delta-value {
  color: #e0685d;
}

.arrow {
  color: rgba(255, 255, 255, 0.3);
}

.delta-none {
  margin: 0;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.74rem;
}

.action {
  min-width: 7rem;
  text-align: right;
}

.action button {
  width: 100%;
  padding: 0.5rem 0.7rem;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 0.35rem;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, filter 0.12s ease;
}

.action button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Fit: the reversible half of the economy — see the header comment on `buy()` —
   so it reads as a plain, sturdy action rather than a spend. */
.action .fit {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

.action .fit:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.34);
}

/* Buy: the accent slab `ItemWorkbench`'s own `.primary` uses, so a spend reads
   the same way on both surfaces. `.gem` swaps in the diamond-shop blue the same
   way it does there. */
.action .buy {
  color: #14100b;
  background: #e8722c;
  border: 1px solid transparent;
}

.action .buy:hover:not(:disabled) {
  filter: brightness(1.08);
}

.action .buy.gem {
  color: #06202e;
  background: #8fd6ff;
}

/* The fitted part's Fit button: locked, green, with a check. */
.action .fitted-lock,
.action .fitted-lock:disabled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  opacity: 1;
  cursor: default;
  color: #8fe09c;
  background: rgba(110, 200, 125, 0.12);
  border: 1px solid rgba(110, 200, 125, 0.55);
}

.fitted-lock svg {
  width: 0.9rem;
  height: 0.9rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}


.blocked {
  margin: 0;
  display: inline-block;
  font-size: 0.7rem;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.35);
}

/* The missing amount is the one figure worth a player's attention in this
   state — the rest of the line is context around it. */
.blocked .missing {
  color: #e0a15d;
  font-weight: 600;
}

/* A locked part names its gate rather than a generic "Research required" —
   the same "Research: \n<name>" shape as `ItemWorkbench`'s own `.gate-link`,
   clickable through to the tree when a node id is on the wire. */
.gate-link {
  padding: 0;
  font: inherit;
  font-size: 0.7rem;
  line-height: 1.3;
  text-align: right;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.45);
  background: none;
  border: 0;
  cursor: pointer;
}

.gate-link strong {
  color: #e8722c;
  text-decoration: underline;
  text-underline-offset: 0.15em;
}

.gate-link:hover strong {
  color: #f38b4c;
}

@media (max-width: 40rem) {
  .slots {
    grid-template-columns: repeat(2, 1fr);
  }

  .part {
    grid-template-columns: 4rem 1fr;
  }

  .action {
    grid-column: 1 / -1;
    text-align: left;
  }
}
</style>
